import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Box, Typography, Card, CardContent, Grid } from '@mui/material';

/**
 * Generate vesting schedule data points for chart
 */
function generateVestingData(allocation, vestingSchedule, startDate, endDate) {
  const data = [];
  const start = new Date(startDate);
  const end = new Date(endDate);
  
  // Generate monthly data points
  for (let date = new Date(start); date <= end; date.setMonth(date.getMonth() + 1)) {
    const { calculateVestedAmount } = require('../utils/vestingUtils');
    const vestingCalc = calculateVestedAmount(vestingSchedule, allocation.allocation, start, date);
    
    data.push({
      date: date.toISOString().split('T')[0],
      dateLabel: date.toLocaleDateString(),
      percentage: Math.min(100, vestingCalc.vestedPercentage),
      vestedAmount: vestingCalc.vestedAmount,
      unvestedAmount: vestingCalc.unvestedAmount
    });
  }
  
  return data;
}

/**
 * Generate combined vesting data for all allocations
 */
function generateCombinedVestingData(allocations, vestingSchedules, startDate, endDate) {
  const data = [];
  const start = new Date(startDate);
  const end = new Date(endDate);
  
  for (let date = new Date(start); date <= end; date.setMonth(date.getMonth() + 1)) {
    let totalAllocation = 0;
    let totalVested = 0;
    
    allocations.forEach(allocation => {
      const amount = parseFloat(allocation.allocation || 0);
      const vestingSchedule = vestingSchedules.find(s => s.id === allocation.vestingType);
      const allocStartDate = new Date(allocation.allocationDate);
      
      if (vestingSchedule && !isNaN(allocStartDate.getTime()) && amount > 0) {
        // Note: This require won't work in browser - need to move calculation to backend
        console.warn('VestingChart: Cannot calculate vesting on client side');
        const vestingCalc = calculateVestedAmount(vestingSchedule, amount, allocStartDate, date);
        totalVested += vestingCalc.vestedAmount;
      } else {
        totalVested += amount; // No vesting = fully vested
      }
      totalAllocation += amount;
    });
    
    const percentage = totalAllocation > 0 ? (totalVested / totalAllocation) * 100 : 0;
    
    data.push({
      date: date.toISOString().split('T')[0],
      dateLabel: date.toLocaleDateString(),
      percentage: Math.min(100, percentage),
      totalVested,
      totalAllocation
    });
  }
  
  return data;
}

/**
 * Vesting Chart Component - Uses backend-generated chart data
 */
function VestingChart({ chartData }) {
  if (!chartData || (!chartData.combined?.length && !chartData.individual?.length)) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
        No vesting data to display
      </Typography>
    );
  }

  return (
    <Box>
      {/* Combined Vesting Chart */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Combined Vesting Schedule
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Total vesting progress across all allocations
          </Typography>
          
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={chartData.combined}>
              <CartesianGrid strokeDasharray="3 3" stroke="#333" />
              <XAxis 
                dataKey="dateLabel" 
                stroke="#fff"
                angle={-45}
                textAnchor="end"
                height={80}
              />
              <YAxis 
                stroke="#fff"
                domain={[0, 100]}
                tickFormatter={(value) => `${value}%`}
              />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: '#2a2a2a', 
                  border: '1px solid #444', 
                  color: '#fff' 
                }}
                formatter={(value, name) => [
                  name === 'percentage' ? `${value.toFixed(1)}%` : value.toLocaleString(),
                  name === 'percentage' ? 'Vested' : name
                ]}
              />
              <Legend />
              <Line 
                type="monotone" 
                dataKey="percentage" 
                stroke="#00e676" 
                strokeWidth={3}
                name="Vested Percentage"
                dot={{ fill: '#00e676', strokeWidth: 2, r: 4 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Individual Allocation Charts */}
      <Typography variant="h6" gutterBottom sx={{ mt: 4, mb: 2 }}>
        Individual Allocation Schedules
      </Typography>
      
      <Grid container spacing={3}>
        {chartData.individual?.map((item, index) => {
          const allocation = item.allocation;
          const allocStartDate = new Date(allocation.allocationDate);
          
          if (isNaN(allocStartDate.getTime())) {
            return (
              <Grid item xs={12} md={6} key={index}>
                <Card>
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom>
                      Allocation #{index + 1} - {allocation.vestingType}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Invalid allocation date
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            );
          }

          // Use data from backend instead of generating client-side

          return (
            <Grid item xs={12} md={6} key={index}>
              <Card>
                <CardContent>
                  <Typography variant="subtitle1" gutterBottom>
                    {allocation.vestingType} - {parseFloat(allocation.allocation).toLocaleString()} MOANI
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                    Start: {allocStartDate.toLocaleDateString()} | Batch: {allocation.csvBatch}
                  </Typography>
                  
                  <ResponsiveContainer width="100%" height={200}>
                    <LineChart data={item.data}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                      <XAxis 
                        dataKey="dateLabel" 
                        stroke="#fff"
                        angle={-45}
                        textAnchor="end"
                        height={60}
                        interval="preserveStartEnd"
                      />
                      <YAxis 
                        stroke="#fff"
                        domain={[0, 100]}
                        tickFormatter={(value) => `${value}%`}
                      />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: '#2a2a2a', 
                          border: '1px solid #444', 
                          color: '#fff' 
                        }}
                        formatter={(value) => [`${value.toFixed(1)}%`, 'Vested']}
                      />
                      <Line 
                        type="monotone" 
                        dataKey="percentage" 
                        stroke="#2196f3" 
                        strokeWidth={2}
                        dot={{ fill: '#2196f3', strokeWidth: 1, r: 3 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
}

export default VestingChart;
